import { useTranslation } from 'react-i18next';
import { AlertTriangle, Check, Loader2, MapPin, Star, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { HotelVendorBadge } from '@/components/hotels/hotel-vendor-badge';
import { cn } from '@/lib/cn';
import { readPlanRowStay } from '@/lib/allocation/stay';
import { isPriced } from '@/lib/allocation/price';
import { StayLine } from './stay-line';
import { Unpriced } from './unpriced';
import type {
  AllocationPlan,
  AllocationPlanItem,
  BookAllResult,
  BookAllStatus,
} from '@/lib/bulk-allocation/types';

// Controlled presentation of an allocation plan: totals + per-group rows
// (PNR · hotel ★ · rooms · $/room · total · compliance · status), the book-all
// summary, and the no-compliant-hotel gaps region. No scope assumptions — it
// renders whatever plan it is handed, so the single-case tab and the
// disruption-event surface share it. When the plan spans multiple airports
// (event scope), rows are grouped into per-airport sections; at single-case
// scope every row shares one airport and it renders flat (no section headers).
//
// Copy lives in i18n under the `cases` namespace (`plan.*`); the reason/status
// helpers below map a wire code to a KEY, never to language.
//
// Money: `$/room`, row total and the plan total are the airline's PRICE (the BFF
// prices the plan, commercial-contract §6.13); `null` → «No disponible».

export interface AllocationPlanTableProps {
  plan: AllocationPlan;
  results: BookAllResult[] | null;
  isBooking: boolean;
  /** Tailwind max-height for the rows scroll region. Defaults to `max-h-72`
   *  (single-case scope, a handful of groups); event scope (hundreds of PNRs)
   *  passes a taller class so the plan is reviewable without scrolling a 5-row
   *  window. */
  maxHeightClass?: string;
}

export function AllocationPlanTable({
  plan,
  results,
  isBooking,
  maxHeightClass = 'max-h-72',
}: AllocationPlanTableProps) {
  const { t } = useTranslation('cases');
  const resultByGroup = new Map((results ?? []).map((r) => [r.subCaseUrn, r]));
  const sections = groupByAirport(plan.plan);
  const showAirportHeaders = sections.length > 1;

  return (
    <>
      {/* Totals (fixed) */}
      <dl className="grid grid-cols-3 gap-x-6 gap-y-1 text-sm">
        <div>
          <dt className="text-xs uppercase text-muted-foreground">{t('plan.totals.groups')}</dt>
          <dd className="text-lg font-semibold">{plan.totals.groups}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase text-muted-foreground">{t('plan.totals.rooms')}</dt>
          <dd className="text-lg font-semibold">{plan.totals.rooms}</dd>
        </div>
        <div>
          <dt className="text-xs uppercase text-muted-foreground">{t('plan.totals.estTotal')}</dt>
          <dd className="text-lg font-semibold">
            {isPriced(plan.totals.totalUSD) ? formatUSD(plan.totals.totalUSD) : <Unpriced className="text-sm" />}
          </dd>
        </div>
      </dl>

      {results ? <BookAllSummary results={results} /> : null}

      {plan.plan.length === 0 ? (
        <p className="rounded-md border border-dashed border-border py-6 text-center text-sm text-muted-foreground">
          {t('plan.empty')}
        </p>
      ) : (
        // The plan table is the ONLY scroll region here (§18.1) — the bar and
        // totals above stay put.
        // The Stay column made this table wide enough to push a phone/tablet
        // viewport sideways; §18 says wide content scrolls inside its OWN box,
        // never the page. `min-w-[44rem]` keeps the columns readable rather than
        // letting them crush.
        <div
          className={cn(maxHeightClass, 'overflow-y-auto overflow-x-auto rounded-md border border-border')}
        >
          <table className="w-full min-w-[44rem] text-sm" data-testid="plan-table">
            <thead className="sticky top-0 bg-muted/60 text-left text-xs uppercase text-muted-foreground">
              <tr>
                <th className="px-3 py-2 font-medium">{t('plan.col.pnr')}</th>
                <th className="px-3 py-2 font-medium">{t('plan.col.hotel')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('plan.col.rooms')}</th>
                <th className="px-3 py-2 font-medium">{t('plan.col.stay')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('plan.col.ratePerRoom')}</th>
                <th className="px-3 py-2 text-right font-medium">{t('plan.col.total')}</th>
                <th className="px-3 py-2 font-medium">{t('plan.col.compliance')}</th>
                <th className="px-3 py-2 font-medium">{t('plan.col.status')}</th>
              </tr>
            </thead>
            {sections.map((section) => (
              <tbody key={section.airportCode ?? '_'} data-testid="plan-section">
                {showAirportHeaders ? (
                  <tr className="border-t border-border bg-muted/30">
                    <td
                      colSpan={8}
                      className="px-3 py-1.5 text-xs font-semibold uppercase text-muted-foreground"
                    >
                      {section.airportCode ?? t('plan.section.otherAirport')} ·{' '}
                      {t('plan.section.groups', { count: section.items.length })}
                    </td>
                  </tr>
                ) : null}
                {section.items.map((item) => {
                  const result = resultByGroup.get(item.subCaseUrn);
                  return (
                    <tr
                      key={item.subCaseUrn}
                      className="border-t border-border"
                      data-testid="plan-row"
                    >
                      <td className="px-3 py-2 font-mono font-medium">
                        {item.pnr}
                        {item.party ? (
                          <Badge
                            variant="info"
                            size="sm"
                            className="ml-1.5 font-sans"
                            title={t('plan.party.title', { pnrs: item.party.pnrs.join(', ') })}
                            data-testid="plan-party"
                          >
                            {item.party.role === 'MEMBER'
                              ? t('plan.party.with', { pnr: item.party.rootPnr })
                              : t('plan.party.root', { n: item.party.pnrs.length })}
                          </Badge>
                        ) : null}
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-1.5">
                          <span className="truncate">{item.hotelName}</span>
                          <span className="flex shrink-0 items-center text-amber-500">
                            {Array.from({ length: item.stars }).map((_, i) => (
                              <Star key={i} className="h-3 w-3 fill-current" aria-hidden />
                            ))}
                          </span>
                          <HotelVendorBadge urn={item.hotelUrn} className="shrink-0" />
                        </div>
                        <span className="flex items-center gap-1 text-xs text-muted-foreground">
                          <MapPin className="h-3 w-3" aria-hidden />
                          {item.distanceKm.toFixed(1)} km
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {item.roomsNeeded}
                        {item.capacity === undefined ? (
                          <span
                            className="flex items-center justify-end gap-1 text-xs text-amber-600 dark:text-amber-400"
                            title={t('plan.unverified.title')}
                            data-testid="unverified-capacity"
                          >
                            <AlertTriangle className="h-3 w-3" aria-hidden />
                            {t('plan.unverified.label')}
                          </span>
                        ) : null}
                      </td>
                      {/* The stay this row was PRICED for. `totalUSD` below is the
                          whole stay × rooms — what booking books — so a review table without the
                          night count let a four-night total read as a rate typo.
                          The BFF carries it FLAT on the row (`checkInUtc` /
                          `staySource` / …), not nested — see `readPlanRowStay`. */}
                      <td className="px-3 py-2 text-xs">
                        <StayLine plan={readPlanRowStay(item)} compact />
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {isPriced(item.ratePerRoomUSD) ? formatUSD(item.ratePerRoomUSD) : <Unpriced className="text-xs" />}
                      </td>
                      <td className="px-3 py-2 text-right font-medium tabular-nums">
                        {isPriced(item.totalUSD) ? formatUSD(item.totalUSD) : <Unpriced className="text-xs" />}
                      </td>
                      <td className="px-3 py-2">
                        <Badge variant="success" size="sm">
                          <Check className="h-3 w-3" aria-hidden /> {t('plan.compliant')}
                        </Badge>
                      </td>
                      <td className="px-3 py-2">
                        {isBooking && !result ? (
                          <span className="flex items-center gap-1 text-xs text-muted-foreground">
                            <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> {t('plan.booking')}
                          </span>
                        ) : result ? (
                          <ResultBadge result={result} />
                        ) : (
                          <span className="text-xs text-muted-foreground">{t('plan.planned')}</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            ))}
          </table>
        </div>
      )}

      {plan.gaps.length > 0 ? (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-50 p-3 dark:bg-amber-900/20"
          data-testid="plan-gaps"
        >
          <p className="flex items-center gap-1.5 text-sm font-medium text-amber-800 dark:text-amber-300">
            <AlertTriangle className="h-4 w-4" aria-hidden />
            {t('plan.gaps.count', { count: plan.gaps.length })}
          </p>
          <ul className="mt-1 space-y-0.5 text-xs text-amber-800/90 dark:text-amber-300/90">
            {plan.gaps.map((gap) => {
              const reasonKey = gapReasonKey(gap.reason);
              return (
                <li key={gap.subCaseUrn} className="flex items-center gap-2">
                  <span className="font-mono font-medium">{gap.pnr}</span>
                  <span className="text-muted-foreground">
                    {reasonKey ? t(reasonKey) : gap.reason.replace(/_/g, ' ')}
                  </span>
                </li>
              );
            })}
          </ul>
          <p className="mt-1 text-xs text-amber-800/80 dark:text-amber-300/80">
            {t('plan.gaps.hint')}
          </p>
        </div>
      ) : null}
    </>
  );
}

interface AirportSection {
  airportCode: string | undefined;
  items: AllocationPlanItem[];
}

/** Group plan rows by airportCode, preserving first-seen order. One group (flat)
 *  when rows share an airport / carry none — multiple at event scope. */
function groupByAirport(items: AllocationPlanItem[]): AirportSection[] {
  const order: (string | undefined)[] = [];
  const byKey = new Map<string | undefined, AllocationPlanItem[]>();
  for (const item of items) {
    const key = item.airportCode;
    let bucket = byKey.get(key);
    if (!bucket) {
      bucket = [];
      byKey.set(key, bucket);
      order.push(key);
    }
    bucket.push(item);
  }
  return order.map((key) => ({ airportCode: key, items: byKey.get(key)! }));
}

export function BookAllSummary({ results }: { results: BookAllResult[] }) {
  const { t } = useTranslation('cases');
  const booked = results.filter((r) => r.status === 'BOOKED').length;
  const failed = results.filter((r) => r.status === 'FAILED').length;
  const skipped = results.filter((r) => r.status === 'SKIPPED').length;
  return (
    <div className="flex flex-wrap items-center gap-2 text-sm" data-testid="book-all-summary">
      <Badge variant="success" size="sm">
        {t('plan.summary.booked', { count: booked })}
      </Badge>
      {skipped > 0 ? (
        <Badge variant="neutral" size="sm">
          {t('plan.summary.skipped', { count: skipped })}
        </Badge>
      ) : null}
      {failed > 0 ? (
        <Badge variant="warning" size="sm">
          {t('plan.summary.failed', { count: failed })}
        </Badge>
      ) : null}
      {failed === 0 ? (
        <span className="text-xs text-emerald-600">{t('plan.summary.allBooked')}</span>
      ) : (
        <span className="text-xs text-muted-foreground">{t('plan.summary.retryHint')}</span>
      )}
    </div>
  );
}

const STATUS_VARIANT: Record<BookAllStatus, 'success' | 'warning' | 'neutral'> = {
  BOOKED: 'success',
  FAILED: 'warning',
  SKIPPED: 'neutral',
};

function ResultBadge({ result }: { result: BookAllResult }) {
  const { t } = useTranslation('cases');
  return (
    <Badge variant={STATUS_VARIANT[result.status]} size="sm" data-testid="result-badge">
      {result.status === 'BOOKED' ? <Check className="h-3 w-3" aria-hidden /> : null}
      {result.status === 'FAILED' ? <X className="h-3 w-3" aria-hidden /> : null}
      <span className={cn(result.status === 'FAILED' && 'lowercase')}>
        {result.status === 'FAILED'
          ? t(failureReasonKey(result.error))
          : result.status === 'SKIPPED'
            ? t(skipReasonKey(result.reason))
            : t('plan.result.booked')}
      </span>
    </Badge>
  );
}

function formatUSD(value: number): string {
  return `$${Math.round(value).toLocaleString('en-US')}`;
}

/** Gap code → i18n key under `plan.gaps.reason`; null when the raw code is the
 *  only honest thing to show (an unknown code, de-underscored). */
function gapReasonKey(code: string): string | null {
  switch (code) {
    case 'no_compliant_inventory':
      return 'plan.gaps.reason.noCompliantInventory';
    case 'no_capacity':
      // The OPPOSITE of no-compliant-inventory: compliant hotels exist, their
      // rooms are gone (or capped as unverified). Next move: source rooms, not
      // relax the policy.
      return 'plan.gaps.reason.noCapacity';
    case 'allocation_engine_unreachable':
      return 'plan.gaps.reason.engineUnreachable';
    case 'allocation_engine_timeout':
      // The engine is up and likely still allocating server-side — the honest
      // next step is a re-plan, not chasing a dead service.
      return 'plan.gaps.reason.engineTimeout';
    case 'party_root_already_reserved':
      // Allocation party (#806 slice 2): the adult's PNR is already booked, and the
      // engine cannot be told "that hotel" — the desk assigns it by hand.
      return 'plan.gaps.reason.partyRootReserved';
    default:
      return null;
  }
}

function failureReasonKey(code: string | undefined): string {
  switch (code) {
    case 'sold_out':
      return 'plan.result.soldOut';
    case 'lock_contention':
      return 'plan.result.lockContention';
    case 'upstream_timeout':
      return 'plan.result.upstreamTimeout';
    // Another operator re-planned since this plan was reviewed: the BFF books from its
    // stored copy only, so the row was not booked (commercial-contract §6.14).
    case 'plan_changed':
      return 'plan.result.planChanged';
    // booking@v2 prices from the offer; a lapsed offer is refused (§6.15).
    case 'offer_expired':
      return 'plan.result.offerExpired';
    default:
      return 'plan.result.failed';
  }
}

function skipReasonKey(code: string | undefined): string {
  switch (code) {
    case 'already_confirmed':
      return 'plan.result.alreadyConfirmed';
    case 'already_held':
      // The acceptance gate's resting state — the room IS held, the passenger
      // hasn't answered yet. Not an error, and not re-bookable.
      return 'plan.result.alreadyHeld';
    case 'already_reserved':
      return 'plan.result.alreadyReserved';
    default:
      return 'plan.result.skipped';
  }
}
